import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    Stack,
    Divider,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Link,
    Chip,
} from '@mui/material';
import { API_URL } from '../../api';
import EditIcon from '@mui/icons-material/Edit';
import AccountStatusCard from '../../components/AccountStatusCard';
import AccountDangerZone from '../../components/AccountDangerZone';
import LinkedAccountsCard from '../../components/LinkedAccountsCard';
import EditAccountDetailsDialog from '../../components/EditAccountDetailsDialog';
import AccountRollupCards from '../../components/AccountRollupCards';
import YearGroupHistoryCard from '../../components/YearGroupHistoryCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const Field = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
        <Typography color="text.secondary">{label}</Typography>
        <Typography>{value || '—'}</Typography>
    </Box>
);

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function ParentDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [parent, setParent] = useState(null);
    const [teachers, setTeachers] = useState([]);
    const [tutorId, setTutorId] = useState('');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [editOpen, setEditOpen] = useState(false);
    const [childTab, setChildTab] = useState(0);

    const load = async () => {
        const { data } = await axios.get(`${API_URL}/api/admin/accounts/${id}`, authHeaders());
        setParent(data);
        setTutorId(data?.assignedTutorId?._id || '');
    };

    useEffect(() => {
        load();
        axios
            .get(`${API_URL}/api/admin/accounts?role=teacher&limit=200`, authHeaders())
            .then((res) => setTeachers(res.data?.items || []))
            .catch(() => setTeachers([]));
    }, [id]);

    const assign = async () => {
        setBusy(true);
        setMessage('');
        setError('');
        try {
            await axios.post(
                `${API_URL}/api/admin/accounts/${id}/assign-tutor`,
                { tutorId: tutorId || null },
                authHeaders()
            );
            setMessage('Tutor assignment updated.');
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    if (!parent) {
        return (
            <Container>
                <Stack alignItems="center" sx={{ py: 6 }}>
                    <CircularProgress />
                </Stack>
            </Container>
        );
    }

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h4">
                    {parent.firstName} {parent.surname}
                </Typography>
                <Button component={RouterLink} to="/parents">
                    Back
                </Button>
            </Stack>

            <AccountStatusCard
                account={parent}
                onChanged={(next) => next && setParent(next)}
            />

            <AccountDangerZone account={parent} />

            <LinkedAccountsCard account={parent} onChange={load} />

            <Card sx={{ p: 3, mb: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">Contact</Typography>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                        Edit details
                    </Button>
                </Stack>
                <Field
                    label="Existing parent?"
                    value={parent.isExistingParent ? 'Yes — self-declared at signup' : 'No / not stated'}
                />
                <Field label="Email" value={parent.email} />
                <Field label="Phone" value={parent.phone} />
                <Field
                    label="Address"
                    value={
                        [
                            parent.homeAddress?.line1,
                            parent.homeAddress?.line2,
                            parent.homeAddress?.city,
                            parent.homeAddress?.postcode,
                        ]
                            .filter(Boolean)
                            .join(', ')
                    }
                />
            </Card>

            <ChildrenSection parent={parent} childTab={childTab} setChildTab={setChildTab} />

            <EditAccountDetailsDialog
                open={editOpen}
                account={parent}
                onClose={() => setEditOpen(false)}
                onSaved={(next) => next && setParent(next)}
            />

            <AccountRollupCards account={parent} />

            <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Primary tutor (individual assignment)
                </Typography>
                <Typography variant="caption" component="div" color="text.secondary" sx={{ mb: 2 }}>
                    A single "primary tutor" for individual / one-off tuition needs. Independent of tutoring class
                    rosters — <strong>if this family is on a class, the class's tutor is the one who actually teaches
                    them</strong>. Use this field when a family has a personal tutor outside a class (e.g. exam-prep
                    one-to-ones) so admin can quickly see who to contact.
                </Typography>
                {message && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {message}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Stack direction="row" spacing={2}>
                    <TextField
                        select
                        label="Primary tutor"
                        value={tutorId}
                        onChange={(e) => setTutorId(e.target.value)}
                        sx={{ minWidth: 280 }}
                        size="small"
                    >
                        <MenuItem value="">— None —</MenuItem>
                        {teachers.map((t) => {
                            const teaches = t.teachingSubjects?.length
                                ? ' — ' +
                                  t.teachingSubjects
                                      .map((s) => `${s.subject}${s.levels?.length ? '/' + s.levels.join(',') : ''}`)
                                      .join('; ')
                                : '';
                            return (
                                <MenuItem key={t._id} value={t._id}>
                                    {t.firstName} {t.surname}{teaches}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                    <Button variant="contained" disabled={busy} onClick={assign}>
                        {busy ? 'Saving…' : 'Save'}
                    </Button>
                </Stack>
            </Card>
        </Container>
    );
}

/**
 * "Children" card — one tab per linked student account (via parentId),
 * plus a first tab for the legacy self-declared childInfo (only rendered
 * when there's no linked account for that same person yet). Each tab
 * shows contact-y details + the enrolled classes currentClasses came
 * back with in getAccount().
 */
function ChildrenSection({ parent, childTab, setChildTab }) {
    const linked = Array.isArray(parent.linkedChildren) ? parent.linkedChildren : [];
    const legacyChild = parent.childInfo?.studentName ? parent.childInfo : null;
    // Show legacy tab only if the legacy child hasn't been converted to a linked account.
    const showLegacy = legacyChild
        && !linked.some((c) => `${c.firstName || ''} ${c.surname || ''}`.trim().toLowerCase() === String(legacyChild.studentName).trim().toLowerCase());
    const tabs = [
        ...linked.map((c) => ({ kind: 'linked', child: c, label: `${c.firstName || ''} ${c.surname || ''}`.trim() || c.email })),
        ...(showLegacy ? [{ kind: 'legacy', child: legacyChild, label: `${legacyChild.studentName} (not linked)` }] : []),
    ];
    if (tabs.length === 0) {
        return (
            <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Children</Typography>
                <Typography color="text.secondary">
                    No children linked. Invite one via <em>Family accounts</em> above, or ask the parent to invite from their portal.
                </Typography>
            </Card>
        );
    }
    const activeIdx = Math.min(childTab, tabs.length - 1);
    const active = tabs[activeIdx];
    return (
        <Card sx={{ p: 3, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Children ({tabs.length})</Typography>
            </Stack>
            <Tabs
                value={activeIdx}
                onChange={(_, v) => setChildTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
                {tabs.map((t, i) => <Tab key={i} label={t.label} />)}
            </Tabs>
            {active.kind === 'linked' ? (
                <LinkedChildTab child={active.child} />
            ) : (
                <LegacyChildTab child={active.child} history={parent.childInfo?.yearGroupHistory} />
            )}
        </Card>
    );
}

function LinkedChildTab({ child }) {
    const ci = child.childInfo || {};
    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Link component={RouterLink} to={`/students/${child._id}`} underline="hover" sx={{ fontWeight: 600 }}>
                    Open student account →
                </Link>
                <Chip
                    size="small"
                    label={child.accountStatus || 'unknown'}
                    color={child.accountStatus === 'approved' ? 'success' : child.accountStatus === 'pending_approval' ? 'warning' : 'default'}
                />
                {child.engagementStatus && (
                    <Chip
                        size="small"
                        label={child.engagementStatus === 'enrolled' ? 'Enrolled' : child.engagementStatus === 'on_break' ? 'On break' : 'Not enrolled'}
                        color={child.engagementStatus === 'enrolled' ? 'secondary' : 'default'}
                    />
                )}
            </Stack>
            <Field label="Email" value={child.email} />
            <Field label="Level" value={ci.level} />
            <Field label="Subject" value={ci.subject} />
            <Field label="Year group" value={ci.yearGroup} />
            <Field label="School" value={ci.school} />
            <Field
                label="Date of birth"
                value={ci.dateOfBirth || child.dateOfBirth
                    ? new Date(ci.dateOfBirth || child.dateOfBirth).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '—'}
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Enrolled classes ({(child.currentClasses || []).length})</Typography>
            {(child.currentClasses || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">Not on any published class.</Typography>
            ) : (
                <Stack spacing={0.5}>
                    {(child.currentClasses || []).map((c) => (
                        <Link
                            key={c._id}
                            component={RouterLink}
                            to={`/tutoring-classes/${c._id}`}
                            underline="hover"
                            sx={{ fontSize: 14 }}
                        >
                            {c.name}
                            {c.dayOfWeek != null ? ` · ${WEEKDAYS[c.dayOfWeek]} ${c.startsAt || ''}` : ''}
                            {c.tutor ? ` · ${c.tutor}` : ''}
                        </Link>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

function LegacyChildTab({ child, history }) {
    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                This child was declared during the parent's signup form but doesn't have their own portal account yet.
                Invite them via <em>Family accounts</em> at the top of the page to create a linked account.
            </Alert>
            <Field label="Student name" value={child.studentName} />
            <Field label="Level" value={child.level} />
            <Field label="Subject" value={child.subject} />
            <Field label="Year group" value={child.yearGroup} />
            <Field label="School" value={child.school} />
            <Field
                label="Date of birth"
                value={child.dateOfBirth
                    ? new Date(child.dateOfBirth).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '—'}
            />
            <YearGroupHistoryCard currentYearGroup={child.yearGroup} history={history} />
        </Box>
    );
}
